import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { collection, onSnapshot, updateDoc, doc, writeBatch } from 'firebase/firestore';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import { Card, CardTitle, CardBody } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { theme } from '../../theme';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase/config';
import type { NotificationData } from '../../services/notificationService';
import { useClientPagination } from '../../hooks/useClientPagination';
import { notificationCreatedAtMs } from '../../utils/notificationCreatedAt';

const PageHeader = styled.div`
  margin-bottom: ${theme.spacing[6]};
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const Sub = styled.p`
  margin: 0;
  color: ${theme.colors.light.text.secondary};
  font-size: ${theme.typography.fontSize.sm};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const Row = styled.div<{ $unread?: boolean }>`
  padding: ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.light.border.light};
  background: ${({ $unread }) => ($unread ? `${theme.colors.primary[500]}08` : theme.colors.white)};
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: ${theme.colors.light.border.medium};
    box-shadow: ${theme.shadows.sm};
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.primary[500]};
    outline-offset: 2px;
  }
`;

const RowTitle = styled.div`
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.light.text.primary};
  margin-bottom: ${theme.spacing[1]};
`;

const RowBody = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
  margin-bottom: ${theme.spacing[2]};
`;

const RowMeta = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.light.text.muted};
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  gap: ${theme.spacing[2]};
`;

const CardHeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[4]};
  padding-bottom: ${theme.spacing[4]};
  border-bottom: 1px solid ${theme.colors.light.border.light};
`;

type Row = NotificationData & { id: string };

function routeForNotification(companyId: string, n: Pick<NotificationData, 'type' | 'complainId'>): string {
  if (n.type === 'complain') {
    if (n.complainId) {
      return `/companies/${companyId}/complains?openComplain=${encodeURIComponent(n.complainId)}`;
    }
    return `/companies/${companyId}/complains`;
  }
  return '/dashboard';
}

const CompanyNotificationsPage: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const { companyData } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);

  const allowed = companyData?.id && companyId === companyData.id;

  const {
    pagedItems: pagedNotifications,
    page: notifPage,
    setPage: setNotifPage,
    totalPages: notifTotalPages,
    usePaging: notifUsePaging,
    rangeStart: notifRangeStart,
    rangeEnd: notifRangeEnd,
    totalItems: notifTotalItems,
  } = useClientPagination(rows, rows.length);

  useEffect(() => {
    if (!allowed || !companyId) {
      setLoading(false);
      return undefined;
    }
    const col = collection(db, 'notifications', 'companies', companyId);
    const unsub = onSnapshot(
      col,
      (snap) => {
        const list: Row[] = snap.docs.map((d) => ({ id: d.id, ...(d.data() as NotificationData) }));
        list.sort((a, b) => notificationCreatedAtMs(b.createdAt) - notificationCreatedAtMs(a.createdAt));
        setRows(list);
        setLoading(false);
      },
      (e) => {
        console.error(e);
        setRows([]);
        setLoading(false);
      }
    );
    return unsub;
  }, [allowed, companyId]);

  const markReadIfNeeded = async (r: Row) => {
    if (!companyId || r.read) return;
    try {
      const ref = doc(db, 'notifications', 'companies', companyId, r.id);
      await updateDoc(ref, { read: true });
    } catch (err) {
      console.error(err);
    }
  };

  const markAllRead = async () => {
    if (!companyId) return;
    const unread = rows.filter((r) => !r.read);
    if (unread.length === 0) return;
    setMarkingAll(true);
    try {
      const chunkSize = 400;
      for (let i = 0; i < unread.length; i += chunkSize) {
        const batch = writeBatch(db);
        unread.slice(i, i + chunkSize).forEach((r) => {
          batch.update(doc(db, 'notifications', 'companies', companyId, r.id), { read: true });
        });
        await batch.commit();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setMarkingAll(false);
    }
  };

  const openNotification = (r: Row) => {
    if (!companyId) return;
    const path = routeForNotification(companyId, r);
    void (async () => {
      await markReadIfNeeded(r);
      navigate(path);
    })();
  };

  if (!companyId) {
    return (
      <DashboardLayout>
        <Card>
          <CardBody>Invalid route.</CardBody>
        </Card>
      </DashboardLayout>
    );
  }

  if (!allowed) {
    return (
      <DashboardLayout>
        <Card>
          <CardBody>You do not have access.</CardBody>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <PageHeader>
        <PageTitle>Notifications</PageTitle>
        <Sub>Company alerts including new branch complains. Unread items are highlighted.</Sub>
      </PageHeader>
      <Card>
        <CardBody>
          <CardHeaderBar>
            <CardTitle style={{ margin: 0 }}>Recent</CardTitle>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={markingAll || rows.every((r) => r.read) || rows.length === 0}
              onClick={() => void markAllRead()}
            >
              {markingAll ? 'Updating…' : 'Mark all as read'}
            </Button>
          </CardHeaderBar>
          {loading ? (
            <p style={{ color: theme.colors.light.text.secondary }}>Loading…</p>
          ) : rows.length === 0 ? (
            <p style={{ color: theme.colors.light.text.secondary }}>No notifications yet.</p>
          ) : (
            <>
            <List>
              {pagedNotifications.map((r) => (
                <Row
                  key={r.id}
                  role="button"
                  tabIndex={0}
                  $unread={!r.read}
                  onClick={() => openNotification(r)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openNotification(r);
                    }
                  }}
                >
                  <RowTitle>{r.title}</RowTitle>
                  <RowBody>{r.body}</RowBody>
                  <RowMeta>
                    <span>
                      {r.type === 'complain' && r.complainId ? `ID ${r.complainId}` : r.type}
                      {' · '}
                      {r.createdAt ? new Date(r.createdAt).toLocaleString() : ''}
                      {!r.read ? ' · Unread' : ''}
                    </span>
                  </RowMeta>
                </Row>
              ))}
            </List>
            {notifUsePaging ? (
              <div
                style={{
                  marginTop: theme.spacing[4],
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: theme.spacing[3],
                }}
              >
                <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.light.text.secondary }}>
                  Showing {notifRangeStart}–{notifRangeEnd} of {notifTotalItems}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2] }}>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={notifPage <= 1}
                    onClick={() => setNotifPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.light.text.primary }}>
                    Page {notifPage} of {notifTotalPages}
                  </span>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={notifPage >= notifTotalPages}
                    onClick={() => setNotifPage((p) => Math.min(notifTotalPages, p + 1))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            ) : null}
            </>
          )}
        </CardBody>
      </Card>
    </DashboardLayout>
  );
};

export default CompanyNotificationsPage;
